/**
 * The document head, and structured data.
 *
 * One hook owns everything a crawler and a link preview read: title, description,
 * canonical, Open Graph, Twitter card, robots, and JSON-LD. Screens call `useSeo(...)`
 * and nothing else touches `document.head`.
 *
 * **What this can and cannot do.** The site is client-rendered on static hosting, so
 * these tags are written by JavaScript after load. Google executes JavaScript and
 * indexes the result, so search is served properly. The social crawlers behind link
 * previews — Facebook, WhatsApp, LinkedIn, Slack — mostly do NOT execute JavaScript, so
 * a shared link shows the defaults baked into `index.html` rather than the page's own
 * card. Fixing that needs prerendering, which needs a Node process in front of the
 * static bundle; it is recorded as an open item rather than pretended away. The static
 * defaults are therefore written to be a decent card for *any* page of this site.
 *
 * Every tag this module writes is marked `data-seo`, and every one is cleared on the
 * next navigation. Without that, tags accumulate: a product page visited after an
 * article would carry both `og:type=article` and `og:type=product`, and the crawler
 * takes whichever it reads first.
 */

import { useEffect } from 'react';

/**
 * Where the site is served from. Canonical URLs must be absolute.
 *
 * The `window` fallback is GUARDED because this module is evaluated during the
 * build-time server render, where there is no window and an unguarded read throws a
 * ReferenceError at IMPORT time — taking down the whole render rather than one
 * component. `VITE_SITE_URL` is always set in that environment, so the fallback is
 * only ever reached in a browser.
 */
const SITE_URL = (
  import.meta.env.VITE_SITE_URL ?? (typeof window === 'undefined' ? '' : window.location.origin)
).replace(/\/$/, '');

export interface SeoInput {
  title?: string | null;
  description?: string | null;
  /** Absolute or site-relative. Falls back to the site's default share image. */
  image?: string | null;
  /** Defaults to the current path. Pass one to collapse a filtered URL onto its base. */
  canonicalPath?: string | null;
  type?: 'website' | 'article' | 'product';
  /** Keep this page out of the index — a cart, a checkout, a filtered listing. */
  noIndex?: boolean;
  /** JSON-LD objects. Emitted as one `@graph`. */
  jsonLd?: Record<string, unknown>[];
  /** Article-only. */
  publishedTime?: string | null;
  modifiedTime?: string | null;
}

export interface SeoDefaults {
  siteName: string;
  titleTemplate: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultImage: string;
  allowIndexing: boolean;
}

/**
 * Write a tag, ADOPTING the static one from `index.html` if it is already there.
 *
 * The selector deliberately does not require `[data-seo]`. `index.html` ships a
 * description, an `og:title`, an `og:type` and so on as the fallback a non-JavaScript
 * crawler reads — and looking only for our own tags meant we appended a SECOND
 * `meta[name="description"]` beside the static one on every page. Two descriptions is
 * not a cosmetic problem: a crawler takes whichever it reads first, which was always
 * the generic static one, so no page ever advertised its own description.
 *
 * Adopting instead means the static tags are the managed tags from the first render
 * onwards, and there is exactly one of each.
 */
function upsert(selector: string, create: () => HTMLElement, content: string): void {
  let tag = document.head.querySelector<HTMLElement>(selector);
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  tag.setAttribute('data-seo', '');
  if (tag.tagName === 'LINK') tag.setAttribute('href', content);
  else tag.setAttribute('content', content);
}

function meta(name: string, content: string | null | undefined, attr: 'name' | 'property' = 'name'): void {
  if (!content) return;
  upsert(`meta[${attr}="${name}"]`, () => {
    const el = document.createElement('meta');
    el.setAttribute(attr, name);
    return el;
  }, content);
}

/** Make a URL absolute — Open Graph and canonical both require it. */
function absolute(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

/** Everything the head needs, with every fallback already applied. */
export interface ResolvedSeo {
  title: string;
  ogTitle: string;
  description: string;
  canonical: string;
  image: string | null;
  indexable: boolean;
  type: string;
  publishedTime: string | null;
  modifiedTime: string | null;
  jsonLd: Record<string, unknown>[];
  siteName: string;
}

/**
 * Apply the fallback rules once, so the browser and the build cannot disagree.
 *
 * `applySeo` writes these into `document`; `prerender.mjs` writes the same values into
 * a file. Before this existed the build reimplemented the rules — which title template,
 * which description when a page sets none, when a Twitter card is `summary` rather than
 * `summary_large_image` — and a difference between the two was invisible until someone
 * compared a served file with a rendered page by hand.
 *
 * `pathname` is a parameter rather than a read of `window.location` because there is no
 * window at build time, and the route being rendered is not the process's own location.
 */
export function resolveSeo(input: SeoInput, defaults: SeoDefaults, pathname: string): ResolvedSeo {
  return {
    title: input.title ? defaults.titleTemplate.replace('%s', input.title) : defaults.defaultTitle,
    ogTitle: input.title ?? defaults.defaultTitle,
    description: input.description || defaults.defaultDescription,
    canonical: absolute(input.canonicalPath ?? pathname)!,
    image: absolute(input.image || defaults.defaultImage),
    indexable: defaults.allowIndexing && !input.noIndex,
    type: input.type ?? 'website',
    publishedTime: input.publishedTime ?? null,
    modifiedTime: input.modifiedTime ?? null,
    jsonLd: input.jsonLd ?? [],
    siteName: defaults.siteName,
  };
}

export function applySeo(input: SeoInput, defaults: SeoDefaults): void {
  // Clear everything this module wrote last time. Tags from the previous route are not
  // merged with the current one's — they are replaced.
  document.head.querySelectorAll('[data-seo]').forEach((el) => el.remove());

  const { title, description, canonical, image, indexable } = resolveSeo(input, defaults, window.location.pathname);

  document.title = title;

  meta('description', description);
  meta('robots', indexable ? 'index, follow, max-image-preview:large' : 'noindex, nofollow');

  upsert('link[rel="canonical"]', () => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  }, canonical);

  // Open Graph
  meta('og:site_name', defaults.siteName, 'property');
  meta('og:title', input.title ?? defaults.defaultTitle, 'property');
  meta('og:description', description, 'property');
  meta('og:type', input.type ?? 'website', 'property');
  meta('og:url', canonical, 'property');
  meta('og:locale', 'en_LK', 'property');
  if (image) meta('og:image', image, 'property');
  if (input.publishedTime) meta('article:published_time', input.publishedTime, 'property');
  if (input.modifiedTime) meta('article:modified_time', input.modifiedTime, 'property');

  // Twitter. `summary_large_image` only when there IS an image — the large card with no
  // picture renders worse than the small one.
  meta('twitter:card', image ? 'summary_large_image' : 'summary');
  meta('twitter:title', input.title ?? defaults.defaultTitle);
  meta('twitter:description', description);
  if (image) meta('twitter:image', image);

  if (input.jsonLd?.length) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', '');
    // A single `@graph` rather than several blocks: one parse, one place to look, and
    // nodes can reference each other by `@id`.
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': input.jsonLd });
    document.head.appendChild(script);
  }
}

/* -------------------------------------------------------------------------- */
/* Structured data                                                             */
/* -------------------------------------------------------------------------- */

export interface OrgInput {
  name: string;
  url: string;
  logo: string;
  email?: string | null;
  phone?: string | null;
  streetAddress?: string | null;
  addressLocality?: string | null;
  socials?: string[];
}

/**
 * The company. Emitted on every page.
 *
 * `LocalBusiness` rather than plain `Organization`: this firm has premises people visit
 * and a telephone people ring, and the local type is what earns a map placement for
 * "POS system Homagama". `@id` is stable so other nodes on other pages point at the
 * same entity rather than declaring a new company each time.
 */
export function organizationLd(o: OrgInput): Record<string, unknown> {
  return {
    '@type': ['Organization', 'LocalBusiness'],
    '@id': `${o.url}/#organization`,
    name: o.name,
    url: o.url,
    logo: { '@type': 'ImageObject', url: absolute(o.logo) },
    image: absolute(o.logo),
    ...(o.email ? { email: o.email } : {}),
    ...(o.phone ? { telephone: o.phone } : {}),
    address: {
      '@type': 'PostalAddress',
      ...(o.streetAddress ? { streetAddress: o.streetAddress } : {}),
      ...(o.addressLocality ? { addressLocality: o.addressLocality } : {}),
      addressCountry: 'LK',
    },
    areaServed: { '@type': 'Country', name: 'Sri Lanka' },
    ...(o.socials?.length ? { sameAs: o.socials } : {}),
  };
}

export function websiteLd(siteUrl: string, name: string): Record<string, unknown> {
  return {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name,
    publisher: { '@id': `${siteUrl}/#organization` },
    // Declares the site search so Google can offer a search box in the result.
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/shop?search={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };
}

/** The trail. Google renders it in place of the raw URL in a result. */
export function breadcrumbLd(trail: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: absolute(crumb.path),
    })),
  };
}

export interface ProductLdInput {
  name: string;
  description?: string | null;
  sku: string;
  brand?: string | null;
  image?: string | null;
  price: string;
  currency: string;
  path: string;
  inStock: boolean;
  allowBackorder: boolean;
}

/**
 * A product, with a real offer.
 *
 * Availability is the field that matters and the one most often faked. It is derived
 * from actual stock: `InStock`, `BackOrder` where the product is explicitly orderable
 * past zero, `OutOfStock` otherwise. Claiming availability a shop does not have is
 * exactly what gets a merchant's rich results suppressed.
 */
export function productLd(p: ProductLdInput): Record<string, unknown> {
  const availability = p.inStock
    ? 'https://schema.org/InStock'
    : p.allowBackorder
      ? 'https://schema.org/BackOrder'
      : 'https://schema.org/OutOfStock';

  return {
    '@type': 'Product',
    name: p.name,
    ...(p.description ? { description: p.description } : {}),
    sku: p.sku,
    ...(p.brand ? { brand: { '@type': 'Brand', name: p.brand } } : {}),
    ...(p.image ? { image: absolute(p.image) } : {}),
    offers: {
      '@type': 'Offer',
      url: absolute(p.path),
      priceCurrency: p.currency,
      price: p.price,
      availability,
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@id': `${SITE_URL}/#organization` },
    },
  };
}

export function articleLd(a: {
  headline: string;
  description?: string | null;
  image?: string | null;
  path: string;
  published?: string | null;
  modified?: string | null;
  author?: string | null;
}): Record<string, unknown> {
  return {
    '@type': 'Article',
    headline: a.headline,
    ...(a.description ? { description: a.description } : {}),
    ...(a.image ? { image: absolute(a.image) } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': absolute(a.path) },
    ...(a.published ? { datePublished: a.published } : {}),
    ...(a.modified ? { dateModified: a.modified } : {}),
    author: a.author ? { '@type': 'Person', name: a.author } : { '@id': `${SITE_URL}/#organization` },
    publisher: { '@id': `${SITE_URL}/#organization` },
  };
}

export function serviceLd(s: { name: string; description: string; path: string }): Record<string, unknown> {
  return {
    '@type': 'Service',
    name: s.name,
    description: s.description,
    url: absolute(s.path),
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'Country', name: 'Sri Lanka' },
  };
}

export function faqLd(items: { question: string; answer: string }[]): Record<string, unknown> {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

/** A collection page — the shop, the work index, the journal. */
export function itemListLd(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: absolute(item.path),
    })),
  };
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * What the last `useSeo` call resolved to, during a server render.
 *
 * `applySeo` writes to `document`, and on the server there is no document — but the
 * head still has to be baked into the prerendered file. Rather than reimplement the
 * title, description and JSON-LD rules in the build script (a second copy to keep in
 * step, which is exactly the drift this codebase keeps paying for), the render RECORDS
 * what it resolved and the build reads it back.
 *
 * Deliberately module state, not context: `renderToString` is synchronous and
 * single-pass, so one render populates this once and `takeRenderedSeo` empties it
 * before the next. Nothing reads it in a browser.
 */
let renderedSeo: { input: SeoInput; defaults: SeoDefaults } | null = null;

/** Read and clear what the last server render resolved. Build-time only. */
export function takeRenderedSeo(): { input: SeoInput; defaults: SeoDefaults } | null {
  const seo = renderedSeo;
  renderedSeo = null;
  return seo;
}

export function useSeo(input: SeoInput, defaults: SeoDefaults): void {
  // Recorded during RENDER rather than in the effect below, because effects never run
  // on the server. The deepest call wins, which is the page's own — a layout that also
  // called this would have rendered before it.
  if (typeof document === 'undefined') renderedSeo = { input, defaults };

  // Serialised, so the effect re-runs when any field changes without the caller having
  // to memoise the object it passes in — every call site builds one inline.
  const key = JSON.stringify([input, defaults]);
  useEffect(() => {
    applySeo(input, defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export { SITE_URL, absolute as absoluteUrl };
